import { Box, Button } from "@mui/material";

/**
 * SavedViewTabs — Phase 4 (Design Gap Audit item D) reusable component.
 *
 * Matches the prototype's .views row: a horizontal set of pill buttons
 * like "All open 102", "Needs sourcing 115", with the active one
 * highlighted, sitting above a table/filter bar.
 *
 * Per the implementation instructions: this is a purely presentational,
 * frontend-only filter selector. The calling page owns the actual
 * filtering logic (deriving each view's predicate from data it already has
 * loaded) and passes in the resulting counts — this component never
 * fetches data or invents a count. Every count shown MUST be computed from
 * real, already-loaded API data by the calling page, never hardcoded.
 *
 * Props:
 *   views      - array of { key, label, count } (required). `count` should
 *                be a real number derived from existing data (or omitted
 *                for a view where a count doesn't make sense).
 *   activeKey  - the key of the currently active view (required)
 *   onChange   - (key) => void, called when a view is clicked (required)
 */
export default function SavedViewTabs({ views, activeKey, onChange }) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.75,
        flexWrap: "wrap",
        p: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      {views.map((v) => {
        const isActive = v.key === activeKey;
        return (
          <Button
            key={v.key}
            size="small"
            onClick={() => onChange(v.key)}
            sx={{
              height: 30,
              px: 1.5,
              borderRadius: "8px",
              fontSize: 12.5,
              fontWeight: 600,
              textTransform: "none",
              color: isActive ? "primary.main" : "text.secondary",
              bgcolor: isActive ? "primary.light" : "transparent",
              "&:hover": {
                bgcolor: isActive ? "primary.light" : "action.hover",
              },
            }}
          >
            {v.label}
            {typeof v.count === "number" && (
              <Box
                component="span"
                sx={{
                  ml: 0.75,
                  fontSize: 11,
                  bgcolor: isActive
                    ? "rgba(37,99,235,0.15)"
                    : "action.selected",
                  borderRadius: 10,
                  px: 0.75,
                  py: "1px",
                }}
              >
                {v.count}
              </Box>
            )}
          </Button>
        );
      })}
    </Box>
  );
}
